<script>
import { getAuditTrails } from '@/services/loginauditServices'
import { RectangleEllipsis, ChevronLeft, ChevronRight } from 'lucide-vue-next'

export default {
  name: 'LoginAudits',
  components: {
    RectangleEllipsis,
    ChevronLeft,
    ChevronRight,
  },
  data() {
    return {
      startDate: '',
      endDate: '',
      auditData: [],
      loading: false,
      hasSearched: false,
      currentPage: 1,
      limit: 10,
      searchQuery: '',
      errorMessage: '',
    }
  },
  computed: {
    filteredData() {
      if (!this.searchQuery) return this.auditData;

      const query = this.searchQuery.toLowerCase();
      return this.auditData.filter(item =>
        String(item.Username || '').toLowerCase().includes(query) ||
        String(item.FullName || '').toLowerCase().includes(query) ||
        String(item.OrganizationName || '').toLowerCase().includes(query)
      );
    },
    paginatedData() {
      const startIndex = (this.currentPage - 1) * Number(this.limit);
      return this.filteredData.slice(startIndex, startIndex + Number(this.limit));
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredData.length / Number(this.limit)));
    },
  },
  mounted() {
    const today = new Date()
    this.endDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

    const sevenDaysAgo = new Date(today)
    sevenDaysAgo.setDate(today.getDate() - 7)
    this.startDate = `${sevenDaysAgo.getFullYear()}-${String(sevenDaysAgo.getMonth() + 1).padStart(2, '0')}-${String(sevenDaysAgo.getDate()).padStart(2, '0')}`
    this.fetchAuditData()
  },
  methods: {
    async fetchAuditData() {
      if (!this.startDate || !this.endDate) {
        this.errorMessage = 'Select both start and end dates.';
        return
      }

      this.loading = true
      this.hasSearched = true
      this.errorMessage = ''

      const adjustedEndDate = new Date(this.endDate);
      adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
      const formattedEndDate = adjustedEndDate.toISOString().split('T')[0];

      try {
        const result = await getAuditTrails(this.startDate, formattedEndDate)
        this.auditData = (result || []).reverse();
        this.currentPage = 1;
      } catch (error) {
        console.error('Error fetching audit data:', error)
        this.auditData = []
        this.errorMessage = 'Unable to load login audits. Please try again.';
      } finally {
        this.loading = false
      }
    },
    convertToMalaysiaTime(utcDateTime) {
      const utcDate = new Date(`${utcDateTime}Z`);
      const malaysiaDate = new Date(utcDate.getTime() + 8 * 60 * 60 * 1000);
      return malaysiaDate.toISOString().replace('T', ' ').slice(0, 19);
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      const converted = this.convertToMalaysiaTime(dateString)
      return new Date(converted).toLocaleString()
    },
    changePage(page) {
      this.currentPage = Math.min(Math.max(page, 1), this.totalPages);
    },
  },
}
</script>

<template>
  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Security Monitoring</p>
        <h1 class="page-title"><RectangleEllipsis :size="26" /> Login audits</h1>
        <p class="page-subtitle">Review sign-in activity by user, full name, and organization within a selected date range.</p>
      </div>
      <button class="ui-button primary" @click="fetchAuditData" :disabled="loading">
        <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-search'"></i>
        {{ loading ? 'Loading...' : 'Search audits' }}
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">Total records</span>
        <strong class="stat-value">{{ auditData.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Filtered</span>
        <strong class="stat-value">{{ filteredData.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Range</span>
        <strong class="stat-value stat-text">{{ startDate }} → {{ endDate }}</strong>
      </div>
    </div>

    <div v-if="errorMessage" class="alert-message">
      <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
    </div>

    <div class="ui-card module-filter-card">
      <div class="form-grid three">
        <div class="form-section">
          <label class="field-label">Start date</label>
          <input type="date" v-model="startDate" class="ui-input">
        </div>
        <div class="form-section">
          <label class="field-label">End date</label>
          <input type="date" v-model="endDate" class="ui-input">
        </div>
        <div class="form-section">
          <label class="field-label">Rows per page</label>
          <select v-model="limit" class="ui-select" @change="currentPage = 1">
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
        <div class="form-section full-width">
          <label class="field-label">Search</label>
          <input type="text" v-model="searchQuery" class="ui-input" placeholder="Search username, full name, or organization">
        </div>
      </div>
    </div>

    <div class="table-card">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Username</th>
            <th>Full name</th>
            <th>Organization</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="4">
              <div class="empty-state">Loading login audit records...</div>
            </td>
          </tr>
          <tr v-else v-for="audit in paginatedData" :key="audit.Username + audit.Timestamp">
            <td data-label="Timestamp">
              <span class="table-primary">{{ formatDate(audit.Timestamp) }}</span>
            </td>
            <td data-label="Username">
              <span class="badge-soft muted">{{ audit.Username || 'Unknown' }}</span>
            </td>
            <td data-label="Full name">{{ audit.FullName || '—' }}</td>
            <td data-label="Organization">{{ audit.OrganizationName || '—' }}</td>
          </tr>
          <tr v-if="!loading && paginatedData.length === 0">
            <td colspan="4">
              <div class="empty-state">No login audits found for the current filters.</div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-row" v-if="filteredData.length">
        <span class="table-secondary">Page {{ currentPage }} of {{ totalPages }}</span>
        <div class="table-actions">
          <button class="icon-button-soft" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
            <ChevronLeft :size="16" />
          </button>
          <button class="icon-button-soft" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.module-filter-card {
  padding: 20px;
  margin-bottom: 18px;
}

.table-actions {
  display: flex;
  gap: 10px;
}

.stat-text {
  font-size: 1rem;
}
</style>
